import React, { Suspense, useContext, useEffect, useState } from "react";
import PropertiesMap from "../../Components/PropertiesMap";
import { DataContext } from "../../Context/Context";
import axios from "axios";
import "./style.css";
import { Helmet } from "react-helmet";

import "swiper/css";
import "swiper/css/navigation";
import { differenceInDays, eachDayOfInterval, subDays } from "date-fns";
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import DynamicSeoPage from "../DynamicSeoPage";
import { returnPriceWithTasxes } from "../../utilities/returnPriceWithTaxes";
import DynamicPriceCalculator from "../../utilities/DynamicPriceCalculator";
import locations from "../../utilities/locationData";

const formatPrice = (price) => {
  if (!price) return "N/A";
  return price.toLocaleString("en-AE", {
    style: "currency",
    currency: "AED",
    minimumFractionDigits: 0,
  });
};

const PropertiesOnMap = () => {
  const { startDate, endDate, setEndDate, setStartDate } =
    useContext(DataContext);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calendarData, setCalendarData] = useState({});
  const [visibleListings, setVisibleListings] = useState(10);

  const { location: locationParam } = useParams();
  const [searchParams] = useSearchParams();

  const formatDate = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Parse guest counts from URL params
  const guestCounts = {
    guest: parseInt(searchParams.get("guest")) || 0,
    child: parseInt(searchParams.get("child")) || 0,
    infants: parseInt(searchParams.get("infants")) || 0,
  };

  // Convert URL slug back to location name for filtering
  const selectedLocation = locationParam
    ? locationParam.replace(/-/g, " ")
    : null;

  useEffect(() => {
    const fetchListingsAndCalendar = async () => {
      setLoading(true);
      try {
        const apiUrl = "https://365luxuryhomes.com/api/listings";
        const params = {
          availabilityDateStart: formatDate(startDate),
          availabilityDateEnd: formatDate(endDate),
        };

        const listingsResponse = await axios.get(apiUrl, {
          params,
        });

        const listingsData = listingsResponse?.data || [];

        if (selectedLocation) {
          const filteredListings = listingsData?.filter((listing) => {
            const location = selectedLocation?.toLowerCase();
            const cityMatch = listing?.city?.toLowerCase().includes(location);
            const addressMatch = listing?.address
              ?.toLowerCase()
              .includes(location);
            return cityMatch || (!cityMatch && addressMatch);
          });
          setListings(filteredListings);
        } else {
          setListings(listingsData);
        }

        // Fetch calendar data for each listing
        const calendarPromises = listingsData.map(async (listing) => {
          const response = await axios.get(
            `https://365luxuryhomes.com/api/listing/calendar`,
            {
              params: {
                listingId: listing?.id,
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
              },
            },
          );
          return { [listing?.id]: response.data };
        });

        const calendarResults = await Promise.all(calendarPromises);
        const calendarObj = Object.assign({}, ...calendarResults);
        setCalendarData(calendarObj);
      } catch (error) {
        console.error("Error fetching listings and calendar:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchListingsAndCalendar();
  }, [startDate, endDate, selectedLocation, locationParam]);

  const calculateTotalPrice = (calendar, dateRange) => {
    if (!Array.isArray(calendar) || !dateRange) return 0;

    return dateRange.reduce((total, date) => {
      const day = calendar.find((item) => item.date === date);
      if (day && day.isAvailable) {
        return total + (day.price || 0);
      }
      return total;
    }, 0);
  };

  const getStartDatePrice = (calendar, startDate) => {
    if (!Array.isArray(calendar) || !startDate) return null;

    const day = calendar.find((item) => item.date === startDate);
    return day && day.isAvailable ? day.price : null;
  };

  const dateRange =
    startDate && endDate
      ? eachDayOfInterval({
          start: new Date(startDate),
          end: subDays(new Date(endDate), 1),
        }).map((date) => formatDate(date))
      : [];

  const handleChange = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const handleLoadMore = () => {
    setVisibleListings((prev) => prev + 10);
  };

  const totalNights = differenceInDays(endDate, startDate);

  // Get location data based on selected location
  const locationData = locationParam
    ? locations.find(
        (item) => item.slug.toLowerCase() === locationParam?.toLowerCase(),
      )
    : null;

  // Debug: Log to verify location matching
  console.log("Location Param:", locationParam);
  console.log(
    "Available locations:",
    locations.map((l) => l.slug),
  );
  console.log("Found locationData:", locationData ? locationData.name : "None");

  // Generate dynamic SEO based on location or default to Dubai
  const generateSEO = () => {
    if (locationData) {
      // Determine property type based on location
      const propertyType =
        locationData.slug === "palm-jumeirah" ||
        locationData.slug === "district-one"
          ? "Villas & Apartments"
          : locationData.slug === "jumeirah-village-circle"
            ? "Family Homes"
            : "Apartments";

      // Get unique feature from tagline or intro
      const uniqueFeature = locationData.tagline || "";

      return {
        title: `Luxury Holiday Homes in ${locationData.name} | Short Term Rentals Dubai | 365 Luxury`,
        description: `Stay in the heart of ${locationData.name}. Book exclusive ${propertyType} with concierge service, ${uniqueFeature.toLowerCase()}, and premium amenities. View our ${locationData.name} collection.`,
      };
    } else {
      // Default SEO when no location is selected
      return {
        title:
          "Luxury Holiday Homes in Dubai | Short Term Rentals Dubai | 365 Luxury",
        description:
          "Stay in the heart of Dubai. Book exclusive Villas & Apartments with concierge service, world-class luxury & entertainment, and premium amenities. View our Dubai collection.",
      };
    }
  };

  const seoData = generateSEO();

  const scrollToLocationDetails = () => {
    const detailsSection = document.getElementById("location-details");
    if (detailsSection) {
      detailsSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section className="lg:pt-24 md:pt-24 pt-24 pb-10">
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      {loading ? (
        <div className="container">
          <div className="skeleton h-96 w-full flex items-center justify-center">
            <span className="loading loading-ring loading-lg"></span>
          </div>
        </div>
      ) : (
        <>
          {/* Location Banner - Only shown when location is selected */}
          {locationData && (
            <div className="container mb-6">
              <div className="w-full rounded-lg overflow-hidden shadow-lg flex lg:flex-row md:flex-row flex-col">
                <img
                  src={locationData.image}
                  alt={locationData.name}
                  className="lg:w-[40%] md:w-[40%] w-full h-[250px] lg:h-[280px] md:h-[280px] object-cover object-top"
                />
                <div className="bg-white p-4 lg:p-6 lg:w-[60%] md:w-[60%] w-full flex flex-col justify-center">
                <div className="flex items-center justify-between mb-3">
                      <h1 className="text-xl lg:text-2xl font-medium text-[#74716d]">
                        {locationData.name}
                      </h1>
                      {/* <button
                        onClick={scrollToLocationDetails}
                        className="px-4 py-2 text-[14px] bg-[#aca9a5] hover:bg-[#6c675b] text-white rounded-md transition-colors duration-200 whitespace-nowrap ml-4"
                      >
                        Read More
                      </button> */}
                    </div>

                    {/* Intro - Always visible */}
                    <p className="text-[14px] lg:text-[15px] text-[#74716d] leading-relaxed ">
                      {locationData.intro}
                    </p>
                </div>
              </div>
            </div>
          )}

          <div className="mb-8 mt-10">
            <div className="container flex items-center justify-between ">
              <div className="flex items-center gap-1">
                <span className="lg:text-xl md:text-[18px] text-[16px] font-medium leading-none text-[#74716d]">
                  {listings?.length}
                </span>
                <span className="lg:text-xl md:text-[18px] text-[16px] font-medium leading-none text-[#74716d]">
                  Properties found
                </span>
              </div>
              {/* <Link
                to={"/properties"}
                onClick={handleChange}
                className="px-6 py-2 text-[15px] bg-[#aca9a5] hover:bg-[#6c675b] text-white rounded-md hover:text-white h-[50px] flex items-center justify-center focus:text-white active:text-white hover:no-underline"
              >
                Close Map
              </Link> */}
            </div>
          </div>
          <div className="container px-[20px] flex lg:flex-row md:flex-col-reverse flex-col-reverse items-start gap-8">
            <div className="lg:w-[50%] md:w-full w-full flex flex-col gap-5">
              {listings.slice(0, visibleListings).map((listing) => (
                <Link
                  key={listing.id}
                  to={`/listing/${listing.slug}?statDate=${formatDate(
                    startDate,
                  )}&endDate=${formatDate(endDate)}&guest=${
                    guestCounts?.guest || 0
                  }`}
                  className="flex lg:flex-row md:flex-row flex-col lg:gap-5 md:gap-4 gap-2 items-start hover:no-underline focus:no-underline focus:text-[#74716d] active:text-[#74716d]"
                >
                  <img
                    src={`https://365luxuryhomes.com/api/listings/image?url=${encodeURIComponent(
                      listing?.listingImages[0]?.url,
                    )}&w=600&q=70&format=webp`}
                    alt=""
                    loading="lazy"
                    className="lg:w-[50%] md:w-[50%] w-full h-[240px] object-cover rounded-md"
                  />
                  <div className="lg:w-[50%] md:w-[50%] w-full">
                    <h2 className="text-[18px] leading-[24px] lg:mt-2 md:mt-2 mt-0 font-medium text-[#74716d]">
                      {listing?.name}
                    </h2>
                    <div className="flex items-center gap-4 my-2">
                      <div className="flex gap-1 items-center">
                        <span className="text-[15px] font-normal text-[#74716d]">
                          {listing?.personCapacity}
                        </span>
                        <span className="text-[15px] font-normal text-[#74716d]">
                          Guests
                        </span>
                      </div>
                      <div className="flex gap-1 items-center">
                        <span className="text-[15px] font-normal text-[#74716d]">
                          {listing?.bedroomsNumber}
                        </span>
                        <span className="text-[15px] font-normal text-[#74716d]">
                          Bedrooms
                        </span>
                      </div>
                      <div className="flex gap-1 items-center">
                        <span className="text-[15px] font-normal text-[#74716d]">
                          {listing?.bathroomsNumber}
                        </span>
                        <span className="text-[15px] font-normal text-[#74716d]">
                          Bathrooms
                        </span>
                      </div>
                    </div>
                    {startDate && endDate && (
                      <div>
                        <div className="text-[18px] font-medium text-[#74716d] my-2">
                          {formatPrice(
                            getStartDatePrice(
                              calendarData[listing.id],
                              formatDate(startDate),
                            ) * 1.05,
                          ) || "N/A"}
                          /night
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {listing?.listingAmenities?.slice(0, 5).map((amenity) => (
                        <span
                          key={amenity?.id}
                          className="px-[6px] py-[3px] border border-[#aca9a5] text-[#74716d] text-[14px] rounded-md"
                        >
                          {amenity?.amenityName}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
              {visibleListings < listings.length && (
                <button
                  onClick={handleLoadMore}
                  className="px-6 py-2 mt-4 text-[15px] bg-[#aca9a5] hover:bg-[#6c675b] text-white rounded-[4px] hover:text-white focus:text-white active:text-white"
                >
                  Load More
                </button>
              )}
            </div>
            <div className="lg:w-[50%] md:w-full w-full sticky-section rounded-md overflow-hidden">
              <Suspense
                fallback={<div className="skeleton h-full w-full"></div>}
              >
                <PropertiesMap listings={listings} className="properties-map" />
              </Suspense>
            </div>
          </div>

          {/* Location Details Section - Bottom */}
          {locationData && (
            <div id="location-details" className="container px-[20px] !mt-12">
              <div className="bg-white rounded-lg shadow-lg p-6 lg:p-8">
                {/* Lifestyle Section */}
                {locationData.lifestyle && (
                  <div className="mb-6">
                    <h3 className="text-lg lg:text-xl font-semibold text-[#74716d] mb-3">
                      Lifestyle & Attractions
                    </h3>

                    {locationData.lifestyle.dining && (
                      <div className="mb-4">
                        <h4 className="text-[16px] font-medium text-[#74716d] mb-2">
                          Dining
                        </h4>
                        <ul className="list-disc list-inside space-y-1 lg:columns-2 lg:gap-6">
                          {locationData.lifestyle.dining.map((item, idx) => (
                            <li
                              key={idx}
                              className="text-[15px] text-[#74716d] ml-2"
                            >
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {locationData.lifestyle.activities && (
                      <div className="mb-4">
                        <h4 className="text-[16px] font-medium text-[#74716d] mb-2">
                          Activities
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {locationData.lifestyle.activities.map(
                            (item, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-2 px-3 py-2 border border-[#aca9a5] rounded-md"
                              >
                                <div className="flex-shrink-0">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4 text-[#aca9a5]"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                </div>
                                <span className="text-[14px] text-[#74716d]">
                                  {item}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* FAQ Section */}
                {locationData.faq && locationData.faq.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-lg lg:text-xl font-semibold text-[#74716d] mb-3">
                      Frequently Asked Questions
                    </h3>
                    <div className="flex flex-wrap gap-3 items-start">
                      {locationData.faq.map((item, idx) => (
                        <div
                          key={idx}
                          className="collapse collapse-plus bg-base-100 border border-[#aca9a5] w-full lg:w-[calc(50%-6px)]"
                        >
                          <input type="radio" name="location-faq-accordion" />
                          <div className="collapse-title text-[15px] font-semibold text-[#74716d]">
                            {item.q}
                          </div>
                          <div className="collapse-content text-[14px] text-[#74716d]">
                            <p>{item.a}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default PropertiesOnMap;
